import {
  differenceInHours,
  differenceInMinutes,
  format,
  isSameDay,
  isSameYear,
  subDays,
} from 'date-fns'

export interface RelativeTimestamp {
  label: string
  title: string
  dateTime: string
}

const EMPTY: RelativeTimestamp = { label: '-', title: '', dateTime: '' }
const MINUTE_MS = 60_000

const TIME_FMT = 'h:mm a'
const SAME_YEAR_FMT = 'd MMM, h:mm a'
const FULL_FMT = 'd MMM yyyy, h:mm a'

function parseDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function formatRelativeTimestamp(
  value: string | Date | null | undefined,
  now: Date = new Date(),
): RelativeTimestamp {
  const date = parseDate(value)
  if (!date) return EMPTY

  const title = format(date, FULL_FMT)
  const dateTime = date.toISOString()
  const diffMs = now.getTime() - date.getTime()

  if (Math.abs(diffMs) < MINUTE_MS) {
    return { label: 'Just now', title, dateTime }
  }

  // Future timestamps >= 60s skip the relative branches and show the
  // absolute calendar - "in 4m" reads as a bug for activity feeds.
  if (diffMs < 0) {
    const label = isSameYear(date, now)
      ? format(date, SAME_YEAR_FMT)
      : format(date, FULL_FMT)
    return { label, title, dateTime }
  }

  const diffMin = differenceInMinutes(now, date)
  if (diffMin < 60) {
    return { label: `${diffMin}m ago`, title, dateTime }
  }

  if (isSameDay(date, now)) {
    return { label: `${differenceInHours(now, date)}h ago`, title, dateTime }
  }

  if (isSameDay(date, subDays(now, 1))) {
    return { label: `Yesterday ${format(date, TIME_FMT)}`, title, dateTime }
  }

  const label = isSameYear(date, now)
    ? format(date, SAME_YEAR_FMT)
    : format(date, FULL_FMT)
  return { label, title, dateTime }
}
